package app.nestbridge.feature.invitations.ui

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.outlined.Person
import androidx.compose.material3.Icon
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.tooling.preview.PreviewParameter
import androidx.compose.ui.unit.dp
import com.karawek.core.ui.components.CorePrimaryButton
import com.karawek.core.ui.components.CoreScaffold
import com.karawek.core.ui.components.CoreSecondaryButton
import com.karawek.core.ui.theme.CoreShapes
import com.karawek.core.ui.theme.CoreSpacing
import com.karawek.core.ui.theme.CoreTheme
import com.karawek.core.ui.theme.CoreTypography
import com.karawek.core.ui.theme.ThemeMode
import com.karawek.core.ui.theme.ThemeModeProvider
import app.nestbridge.feature.careteam.model.TeamRelationshipRole
import app.nestbridge.feature.invitations.model.Invitation
import app.nestbridge.feature.invitations.model.InvitationDeliveryMethod
import app.nestbridge.feature.invitations.model.InvitationStatus
import app.nestbridge.feature.invitations.model.OrganizationType
import app.nestbridge.feature.invitations.screen.InvitationDetailEvent
import app.nestbridge.feature.invitations.screen.InvitationDetailScreen
import app.nestbridge.feature.invitations.screen.InvitationDetailState
import app.nestbridge.ui.theme.NestBridgeTheme
import com.slack.circuit.codegen.annotations.CircuitInject
import software.amazon.lastmile.kotlin.inject.anvil.AppScope
import kotlin.time.Instant

@CircuitInject(
    screen = InvitationDetailScreen::class,
    scope = AppScope::class,
)
@Composable
fun InvitationDetailUi(
    state: InvitationDetailState,
    modifier: Modifier,
) {
    CoreScaffold(
        title = "Invitation to connect",
        footer = { Footer(state) },
        onAppear = { state.eventSink(InvitationDetailEvent.OnAppear) },
        modifier = modifier,
    ) {
        Column(
            verticalArrangement = Arrangement.spacedBy(CoreSpacing.sm),
            modifier = Modifier.fillMaxSize(),
        ) {
            CardBlock(style = CardBlockStyle.Default) {
                Row(verticalAlignment = Alignment.CenterVertically) {
                    Box(
                        modifier = Modifier
                            .size(40.dp)
                            .clip(CircleShape)
                            .background(CoreTheme.colors.surfaceVariant),
                        contentAlignment = Alignment.Center,
                    ) {
                        Icon(
                            imageVector = Icons.Outlined.Person,
                            contentDescription = null,
                            tint = CoreTheme.colors.textSecondary,
                        )
                    }

                    Column(
                        modifier = Modifier.padding(start = CoreSpacing.md),
                        verticalArrangement = Arrangement.spacedBy(CoreSpacing.xs),
                    ) {
                        Text(
                            state.invitation.inviterDisplayName,
                            style = CoreTypography.title,
                            color = CoreTheme.colors.textPrimary,
                        )
                        inviterSubtitle(state.invitation)?.let { subtitle ->
                            Text(
                                text = subtitle,
                                style = CoreTypography.bodySecondary,
                                color = CoreTheme.colors.textSecondary,
                            )
                        }
                    }
                }
            }

            CardBlock(style = CardBlockStyle.Default) {
                Text(
                    text = collaborationSummary(state.invitation),
                    style = CoreTypography.bodySecondary,
                    color = CoreTheme.colors.textPrimary,
                )
                collaborationNote(state.invitation)?.let { note ->
                    Spacer(modifier = Modifier.height(CoreSpacing.sm))
                    Text(
                        text = note,
                        style = CoreTypography.bodySecondary,
                        color = CoreTheme.colors.textSecondary,
                    )
                }
            }

            CardBlock(style = CardBlockStyle.Default) {
                Text(
                    "AUTHORITY & ACCESS",
                    style = CoreTypography.caption,
                    color = CoreTheme.colors.textSecondary,
                )
                Spacer(modifier = Modifier.height(CoreSpacing.sm))

                Text(
                    text = roleDescription(state.invitation.proposedTeamRole),
                    style = CoreTypography.bodySecondary,
                    color = CoreTheme.colors.textPrimary,
                )
                if (state.invitation.grantsChildStewardship) {
                    Spacer(modifier = Modifier.height(CoreSpacing.sm))
                    Text(
                        text = "Child-level stewardship will be granted for ${state.invitation.childDisplayName}.",
                        style = CoreTypography.bodySecondary,
                        color = CoreTheme.colors.textSecondary,
                    )
                }
            }

            CardBlock(style = CardBlockStyle.Default) {
                Text(
                    "WHAT ACCEPTING MEANS",
                    style = CoreTypography.caption,
                    color = CoreTheme.colors.textSecondary,
                )
                Spacer(modifier = Modifier.height(CoreSpacing.sm))

                acceptanceBullets(state.invitation).forEach { entry ->
                    BulletEntry(text = entry)
                }
            }

            if (state.requiresVerification) {
                CardBlock(style = CardBlockStyle.Warning) {
                    Text(
                        "VERIFICATION",
                        style = CoreTypography.caption,
                        color = CoreTheme.colors.warningAccent,
                    )
                    Spacer(modifier = Modifier.height(CoreSpacing.sm))
                    verificationBullets(state.invitation).forEach { entry ->
                        BulletEntry(
                            text = entry,
                            bulletColor = CoreTheme.colors.warningAccent,
                        )
                    }
                }
            }

            Text(
                text = "Reviewing this invitation does not grant access.",
                style = CoreTypography.bodySecondary,
                color = CoreTheme.colors.textSecondary,
                textAlign = TextAlign.Center,
                modifier = Modifier.fillMaxWidth(),
            )

            Spacer(modifier = Modifier.height(CoreSpacing.xl))
        }
    }
}

@Composable
private fun Footer(state: InvitationDetailState) =
    Column(
        verticalArrangement = Arrangement.spacedBy(CoreSpacing.sm),
        modifier = Modifier
            .fillMaxWidth()
            .padding(top = CoreSpacing.sm),
    ) {
        CorePrimaryButton(
            text = "Accept invitation",
            onClick = { state.eventSink(InvitationDetailEvent.Accept) },
            modifier = Modifier
                .fillMaxWidth(),
        )

        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.spacedBy(CoreSpacing.sm),
        ) {
            CoreSecondaryButton(
                text = "Decide later",
                onClick = { state.eventSink(InvitationDetailEvent.DecideLater) },
                modifier = Modifier.weight(1f),
            )
            CoreSecondaryButton(
                text = "Decline",
                onClick = { state.eventSink(InvitationDetailEvent.Decline) },
                modifier = Modifier.weight(1f),
            )
        }
    }

@Composable
private fun CardBlock(
    style: CardBlockStyle,
    content: @Composable ColumnScope.() -> Unit,
) {
    val colors = cardBlockColors(style)
    Surface(
        modifier = Modifier.fillMaxWidth(),
        shape = CoreShapes.lg,
        color = colors.background,
        border = BorderStroke(1.dp, colors.outline),
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .padding(CoreSpacing.lg),
            content = content,
        )
    }
}

@Composable
private fun BulletEntry(
    text: String,
    bulletColor: Color = CoreTheme.colors.primary,
) {
    Row(
        modifier = Modifier.fillMaxWidth(),
        verticalAlignment = Alignment.Top,
    ) {
        Box(
            modifier = Modifier
                .padding(top = 6.dp, end = CoreSpacing.sm)
                .size(6.dp)
                .clip(CircleShape)
                .background(bulletColor),
        )
        Text(
            text,
            style = CoreTypography.bodySecondary,
            color = CoreTheme.colors.textPrimary,
        )
    }
    Spacer(modifier = Modifier.height(CoreSpacing.sm))
}

private fun collaborationSummary(invitation: Invitation): String {
    val teamLabel = invitation.teamType?.label ?: "this care team"
    return if (invitation.teamId != null) {
        "You're being invited to join ${teamLabel}."
    } else {
        "You're being invited to create a new care team for ${invitation.childDisplayName}."
    }
}

private fun roleDescription(role: TeamRelationshipRole): String {
    return when (role) {
        TeamRelationshipRole.ORGANIZER ->
            "You will join as an organizer with authority to guide how this care team works."
        TeamRelationshipRole.THERAPIST ->
            "You will join as a therapist who can add updates and collaborate with the team."
        TeamRelationshipRole.PARENT ->
            "You will join as a parent who can collaborate with the team and stay up to date."
        TeamRelationshipRole.OBSERVER ->
            "You will join as an observer with view-only access."
    }
}

private fun acceptanceBullets(invitation: Invitation): List<String> {
    val viewLine = "You can view shared notes and updates about ${invitation.childDisplayName}."
    val collaborationLine = when (invitation.proposedTeamRole) {
        TeamRelationshipRole.OBSERVER -> "You will have view-only access."
        else -> "You can collaborate with others on this care team."
    }
    val accessLine = "Your access is limited to ${invitation.childDisplayName} and this team only."
    val exitLine = "You can leave at any time."
    return listOf(
        viewLine,
        collaborationLine,
        accessLine,
        exitLine,
    )
}

private fun inviterSubtitle(invitation: Invitation): String? {
    val organization = invitation.inviterOrganizationName?.takeIf { it.isNotBlank() }
    return if (organization != null) {
        "Care team lead · $organization"
    } else {
        "Care team lead"
    }
}

private fun collaborationNote(invitation: Invitation): String? {
    return when (invitation.inviterOrganizationType) {
        OrganizationType.EARLY_INTERVENTION -> "This care team is part of an early intervention program."
        OrganizationType.SCHOOL_BASED -> "This care team is part of a school-based support plan."
        OrganizationType.DAYCARE -> "This care team supports daily daycare collaboration."
        OrganizationType.ABA_CENTER -> "This care team is part of an ABA program."
        OrganizationType.PRIVATE_PRACTICE -> "This care team is part of a private practice program."
        OrganizationType.OTHER -> "This care team is part of an organization program."
        null -> null
    }
}

private fun verificationBullets(invitation: Invitation): List<String> {
    val profileLine = if (invitation.teamId == null) {
        "Profile linking is allowed when you accept."
    } else {
        "Profile linking is not required for this invitation."
    }
    return listOf(
        "Verification is required before you can accept.",
        profileLine,
    )
}

private enum class CardBlockStyle {
    Default,
    Info,
    Warning,
    Error,
}

private data class CardBlockColors(
    val background: Color,
    val outline: Color,
)

@Composable
private fun cardBlockColors(style: CardBlockStyle): CardBlockColors {
    return when (style) {
        CardBlockStyle.Default -> CardBlockColors(
            background = CoreTheme.colors.surface,
            outline = CoreTheme.colors.divider,
        )
        CardBlockStyle.Info -> CardBlockColors(
            background = CoreTheme.colors.infoSurface,
            outline = CoreTheme.colors.infoOutline,
        )
        CardBlockStyle.Warning -> CardBlockColors(
            background = CoreTheme.colors.warningSurface,
            outline = CoreTheme.colors.warningOutline,
        )
        CardBlockStyle.Error -> CardBlockColors(
            background = CoreTheme.colors.errorSurface,
            outline = CoreTheme.colors.errorOutline,
        )
    }
}

@Preview
@Composable
private fun InvitationDetailPreview(
    @PreviewParameter(ThemeModeProvider::class) themeMode: ThemeMode,
) {
    NestBridgeTheme(themeMode = themeMode) {
        InvitationDetailUi(
            state = InvitationDetailState(
                invitation = Invitation(
                    id = "id",
                    status = InvitationStatus.PENDING,
                    inviterUserProfileId = "person_001",
                    inviterDisplayName = "Dr. Sarah Chen",
                    inviterOrganizationName = "Metro Early Intervention",
                    inviterOrganizationType = OrganizationType.EARLY_INTERVENTION,
                    childId = "child_01",
                    childDisplayName = "Emily Brown",
                    teamId = "team_01",
                    teamType = app.nestbridge.domain.identity.model.ProfessionalServiceType.SpeechLanguageTherapy,
                    proposedTeamRole = app.nestbridge.feature.careteam.model.TeamRelationshipRole.THERAPIST,
                    grantsChildStewardship = false,
                    deliveryMethod = InvitationDeliveryMethod.EMAIL,
                    deliveryDestination = "parent@example.com",
                    createdAt = Instant.fromEpochSeconds(1_713_000_000),
                    createdBy = "person_001",
                    modifiedAt = Instant.fromEpochSeconds(1_713_000_000),
                    modifiedBy = "person_001",
                ),
                requiresVerification = false,
                eventSink = { },
            ),
            modifier = Modifier.background(CoreTheme.colors.background),
        )
    }
}

@Preview
@Composable
private fun InvitationDetailWithRequiredVerificationPreview(
    @PreviewParameter(ThemeModeProvider::class) themeMode: ThemeMode,
) {
    NestBridgeTheme(themeMode = themeMode) {
        InvitationDetailUi(
            state = InvitationDetailState(
                invitation = Invitation(
                    id = "id",
                    status = InvitationStatus.PENDING,
                    inviterUserProfileId = "person_001",
                    inviterDisplayName = "Dr. Sarah Chen",
                    inviterOrganizationName = "Metro Early Intervention",
                    inviterOrganizationType = OrganizationType.EARLY_INTERVENTION,
                    childId = "child_01",
                    childDisplayName = "Emily Brown",
                    teamId = "team_01",
                    teamType = app.nestbridge.domain.identity.model.ProfessionalServiceType.SpeechLanguageTherapy,
                    proposedTeamRole = app.nestbridge.feature.careteam.model.TeamRelationshipRole.THERAPIST,
                    grantsChildStewardship = false,
                    deliveryMethod = InvitationDeliveryMethod.EMAIL,
                    deliveryDestination = "parent@example.com",
                    createdAt = Instant.fromEpochSeconds(1_713_000_000),
                    createdBy = "person_001",
                    modifiedAt = Instant.fromEpochSeconds(1_713_000_000),
                    modifiedBy = "person_001",
                ),
                requiresVerification = true,
                eventSink = { },
            ),
            modifier = Modifier.background(CoreTheme.colors.background),
        )
    }
}
